import { API_VERSIONS, REFERENCE_MODEL_OPTIONS } from "../utils/constants";
import RequestHandler from "./RequestHandler";

function LookupsAPI() {
    const baseLookupsAPI = "lookups";

    async function getRoles() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/user-roles`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getParametersWithUnit(modelId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/models/${modelId}/parameters`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getModelStatuses() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/model-statuses`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getOrganizations() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/organizations`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getLabStorePartners() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/labstore-partners`
            );
            return result;
        } catch (error) {
            console.log(error);
        }
    }

    async function getIndustries() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/industries`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getModels(
        organizationId = undefined,
        allowTranslation = true,
        referenceModelOption = REFERENCE_MODEL_OPTIONS.All
    ) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/models?allowTranslation=${allowTranslation}&referenceModelOption=${referenceModelOption}${
                    organizationId ? `&organizationId=${organizationId}` : ""
                }`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getDeployedModels() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/deployed-models`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getMaterials(isGetSystemGenerated = true) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/materials?get-system-generated=${isGetSystemGenerated}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSuppliers(materialId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/suppliers/${materialId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getTrendMaterials(filters = null) {
        try {
            const result = await RequestHandler.post(
                `${baseLookupsAPI}/trend-materials`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getTrendLots(filters = null) {
        try {
            const result = await RequestHandler.post(
                `${baseLookupsAPI}/trend-lots`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getTrendSuppliers(filters = null) {
        try {
            const result = await RequestHandler.post(
                `${baseLookupsAPI}/trend-suppliers`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUsers() {
        try {
            const result = await RequestHandler.get(`${baseLookupsAPI}/users`);
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSuperiorUsers(organizationId, emailOnly = false) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/organization-users?organizationId=${organizationId}&email-only=${emailOnly}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSuperiorUsersV2(
        organizationId,
        emailOnly = false,
        subOrganizationUsers = false
    ) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/${API_VERSIONS.v2}/organization-users?organizationId=${organizationId}&email-only=${emailOnly}&sub-organization-users=${subOrganizationUsers}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getMaterialGroups() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/material-groups`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getAccessByModelId(modelId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/model-access/${modelId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getAccessByUserId(userId, organizationId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/user-models-access?userId=${userId}&organizationId=${organizationId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getAccessByOrganizationId(organizationId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/organization-models-access/${organizationId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getPartners() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/partners`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getCountries(includeGlobal) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/countries${includeGlobal ? "?all=true" : ""}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getCountryByName(countryName) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/countries/name/${countryName}`
            );
            return result || {};
        } catch (error) {
            console.log(error);
        }
    }

    async function getSupportedAccessories() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/supported-accessories`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getMaterialsMeasurements(filters = null) {
        try {
            const result = await RequestHandler.post(
                `${baseLookupsAPI}/materials-measurements`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getMaterialsScans(filters = null) {
        try {
            const result = await RequestHandler.post(
                `${baseLookupsAPI}/materials-scans`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUsedMaterialsInScans(filters, itemsPerPage, offset = 0) {
        try {
            const skip = offset * itemsPerPage;
            const result = await RequestHandler.post(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/scans/used-materials${
                    itemsPerPage ? `?skip=${skip}&take=${itemsPerPage}` : ""
                }`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUsedSuppliersInScans(materialId = "") {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/scans/used-suppliers${
                    materialId ? `?materialId=${materialId}` : ""
                }`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUsedModelsInScans(materialId = "") {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/scans/used-models${
                    materialId ? `?materialId=${materialId}` : ""
                }`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUsedLotsInScans(materialId = "") {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/scans/used-lots${
                    materialId ? `?materialId=${materialId}` : ""
                }`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getScanners(
        includeDefault = true,
        mapPreferredName = false
    ) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/scanners?includeDefault=${includeDefault}&mapPreferredName=${mapPreferredName}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getBiasCorrections(modelParameterVersionId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/bias-corrections/${modelParameterVersionId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getScannerTypes() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/scanner-types`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getTags() {
        try {
            const result = await RequestHandler.get(`${baseLookupsAPI}/tags`);
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getCurrencies() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/currencies`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getAcceptedProducts() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/accepted-products`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getCouponAcceptedProducts() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/coupon-accepted-products`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getIntegrationPartners() {
        try {
            const result = await RequestHandler.get(
                `v1/${baseLookupsAPI}/integration-partners`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getRecoveryQuestions() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/recoveryQuestions`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getInferiorUsers() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/inferior-users`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserOrganizations() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/user-organizations`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSubscribedProductsDetails(organizationSubscriptionId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/subscribed-product/${organizationSubscriptionId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getDeployedMaterials() {
        try {
            const result = await RequestHandler.get(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/deployed-materials`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getDeployedMaterialParameters() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/deployed-material-parameters`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSubscriptionAccess(subscriptionId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/subscription-access/${subscriptionId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUnits() {
        try {
            const result = await RequestHandler.get(`${baseLookupsAPI}/units`);
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getModelParameterNamesByMaterial(materialId, mapId = false) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/parameters/${materialId}?mapId=${mapId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getUsedParametersByMaterialLookupForScans(materialId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/materials/${materialId}/model-parameters`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getLinkedUnitConversions(baseUnitIds) {
        try {
            const result = await RequestHandler.post(
                `${baseLookupsAPI}/unit-conversions/linked`,
                baseUnitIds
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getAllowedUnitConversions(baseUnitId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/unit-conversions/allowed/${baseUnitId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getModelParameterUnits(modelParameterId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/model-parameters-units/${modelParameterId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getModelParametersByModelId(modelId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/model-parameters/${modelId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getClassifiers() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/classifiers`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getModelParameterProductVersionAvailableUnits(
        modelParameterProductVersionId
    ) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/product-version/available-units/${modelParameterProductVersionId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getLots(materialId) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/lots?materialId=${materialId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSimplifiedModel() {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/simplified-models`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getSupervisedUsers(
        getUserOrganizations = false,
        systemLogs = false
    ) {
        try {
            const result = await RequestHandler.get(
                `${baseLookupsAPI}/supervised-users?get-user-organizations=${getUserOrganizations}&system-logs=${systemLogs}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getcollectMaterials({ itemsPerPage = 20, filters, offset }) {
        try {
            if (filters.createdById) {
                filters.onBehalfOfUsers = [{ value: filters.createdById }];
                delete filters.createdById;
            }
            const skip = offset * itemsPerPage;
            const result = await RequestHandler.post(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/measurements/used-materials?skip=${skip}&take=${itemsPerPage}`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getScansMaterials({ itemsPerPage = 20, filters, offset }) {
        try {
            if (filters.createdById) {
                filters.onBehalfOfUsers = [{ value: filters.createdById }];
                delete filters.createdById;
            }
            const skip = offset * itemsPerPage;
            const result = await RequestHandler.post(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/measurements/used-scan-materials?skip=${skip}&take=${itemsPerPage}`,
                filters
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getProductVersionSupportedAccessories(productVersionId) {
        try {
            const result = await RequestHandler.get(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/product-version/supported-accessories/${productVersionId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getParentModels() {
        try {
            const result = await RequestHandler.get(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/parent-models`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getLinkedModels(modelId) {
        try {
            const result = await RequestHandler.get(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/linked-models/${modelId}`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    async function getLabStoreDevelopers() {
        try {
            const result = await RequestHandler.get(
                `${API_VERSIONS.v1}/${baseLookupsAPI}/developer-organizations`
            );
            return result || [];
        } catch (error) {
            console.log(error);
        }
    }

    return {
        getRoles,
        getParametersWithUnit,
        getOrganizations,
        getModelStatuses,
        getIndustries,
        getLabStorePartners,
        getModels,
        getDeployedModels,
        getMaterialGroups,
        getMaterials,
        getSuppliers,
        getUsers,
        getAccessByModelId,
        getAccessByUserId,
        getAccessByOrganizationId,
        getPartners,
        getCountries,
        getCountryByName,
        getUsedSuppliersInScans,
        getTrendMaterials,
        getTrendLots,
        getTrendSuppliers,
        getSupportedAccessories,
        getMaterialsMeasurements,
        getUsedParametersByMaterialLookupForScans,
        getMaterialsScans,
        getScanners,
        getBiasCorrections,
        getScannerTypes,
        getTags,
        getCurrencies,
        getAcceptedProducts,
        getIntegrationPartners,
        getRecoveryQuestions,
        getInferiorUsers,
        getUserOrganizations,
        getSubscribedProductsDetails,
        getDeployedMaterialParameters,
        getSubscriptionAccess,
        getModelParametersByModelId,
        getUnits,
        getLinkedUnitConversions,
        getAllowedUnitConversions,
        getModelParameterUnits,
        getSuperiorUsers,
        getSuperiorUsersV2,
        getClassifiers,
        getModelParameterProductVersionAvailableUnits,
        getCouponAcceptedProducts,
        getLots,
        getSimplifiedModel,
        getSupervisedUsers,
        getUsedMaterialsInScans,
        getUsedModelsInScans,
        getUsedLotsInScans,
        getModelParameterNamesByMaterial,
        getcollectMaterials,
        getScansMaterials,
        getDeployedMaterials,
        getProductVersionSupportedAccessories,
        getParentModels,
        getLinkedModels,
        getLabStoreDevelopers,
    };
}

export default LookupsAPI();
